/**
 * Subscription mutations: freeze, unfreeze, cancel. Wired to API with cache invalidation.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../../shared/auth/store';
import { handleMutationError } from '../../query';
import { runInvalidationForMutation } from '../../query/runInvalidation';
import type { InvalidationRuleId } from '../../query/invalidateRegistry';
import {
  freezeSubscription,
  unfreezeSubscription,
  cancelSubscription,
  type FreezeUnfreezeParams,
} from './mutations';

export function useSubscriptionActions() {
  const query_client = useQueryClient();
  const customer_id = useAuthStore(s => s.user?.id);

  const invalidateAfterMutation = (rule_id: InvalidationRuleId, dates?: string[]) => {
    if (typeof customer_id !== 'number') {
      return;
    }
    void runInvalidationForMutation(query_client, {
      rule_id,
      customer_id,
      dates,
    });
  };

  const on_subscription_error = (err: unknown, mutation: string) => {
    handleMutationError(err, { mutation });
  };

  const freeze_mutation = useMutation({
    mutationFn: (params: Omit<FreezeUnfreezeParams, 'customer_id'>) =>
      freezeSubscription({
        ...params,
        customer_id: customer_id!,
      }),
    onSuccess: (_data, params) =>
      invalidateAfterMutation('subscription.freeze', Array.isArray(params.dates) ? params.dates : undefined),
    onError: (err) => on_subscription_error(err, 'freezeSubscription'),
  });

  const unfreeze_mutation = useMutation({
    mutationFn: (params: Omit<FreezeUnfreezeParams, 'customer_id'>) =>
      unfreezeSubscription({
        ...params,
        customer_id: customer_id!,
      }),
    onSuccess: (_data, params) =>
      invalidateAfterMutation('subscription.unfreeze', Array.isArray(params.dates) ? params.dates : undefined),
    onError: (err) => on_subscription_error(err, 'unfreezeSubscription'),
  });

  const cancel_mutation = useMutation({
    mutationFn: (customer_subscription_id: number) =>
      cancelSubscription({
        customer_id: customer_id!,
        customer_subscription_id,
      }),
    onSuccess: () => invalidateAfterMutation('subscription.cancel'),
    onError: (err) => on_subscription_error(err, 'cancelSubscription'),
  });

  const confirmAndCancel = async (subscriptionId: number): Promise<boolean> => {
    if (customer_id == null) {
      return false;
    }
    try {
      await cancel_mutation.mutateAsync(subscriptionId);
      return true;
    } catch {
      return false;
    }
  };

  return {
    freezeSubscription: {
      mutate: (params: Omit<FreezeUnfreezeParams, 'customer_id'>) => freeze_mutation.mutate(params),
      mutateAsync: (params: Omit<FreezeUnfreezeParams, 'customer_id'>) => freeze_mutation.mutateAsync(params),
      isPending: freeze_mutation.isPending,
      error: freeze_mutation.error,
    },
    unfreezeSubscription: {
      mutate: (params: Omit<FreezeUnfreezeParams, 'customer_id'>) => unfreeze_mutation.mutate(params),
      mutateAsync: (params: Omit<FreezeUnfreezeParams, 'customer_id'>) => unfreeze_mutation.mutateAsync(params),
      isPending: unfreeze_mutation.isPending,
      error: unfreeze_mutation.error,
    },
    confirmAndCancel,
    cancelSubscription: {
      mutate: (customer_subscription_id: number) => cancel_mutation.mutate(customer_subscription_id),
      mutateAsync: (customer_subscription_id: number) => cancel_mutation.mutateAsync(customer_subscription_id),
      isPending: cancel_mutation.isPending,
      error: cancel_mutation.error,
    },
  };
}
